/*
 * Copyright (c) 2025 by PROS, Inc.  All Rights Reserved.
 * This software is the confidential and proprietary information of
 * PROS, Inc. ("Confidential Information").
 * You may not disclose such Confidential Information, and may only
 * use such Confidential Information in accordance with the terms of
 * the license agreement you entered into with PROS.
 */
import { isChatUiTextOutputResponsePart, isEventResponsePart, isReferenceResponsePart, isMultipartJsonResponse, isChatUiHighchartsChartResponsePart, } from '../client/model.js';
import { MessagePartKind, } from '../model.js';
/**
 * Get the response content with response in outdated format.
 */
export function parseBackwardCompatibleResponse(response) {
    // guard.
    if (isMultipartJsonResponse(response)) {
        return undefined;
    }
    return [
        {
            kind: MessagePartKind.MARKDOWN,
            data: response.answer,
        },
    ];
}
/**
 * Given a text, filter all relevant message part references.
 */
function findJsonRefIds(input) {
    const regex = /{json-ref:([0-9a-z\\-]+)}/g;
    const result = [];
    let match;
    // execute regex.
    match = regex.exec(input);
    while (match !== null) {
        // push found id.
        result.push(match[1]);
        // execute regex.
        match = regex.exec(input);
    }
    return result;
}
/**
 * Filter all relevant response parts that are related to the given markdown text.
 */
function filterRelevantMessagePartReferences(text, referenceResponseParts) {
    // find json ref in the text.
    const jsonRefIds = findJsonRefIds(text);
    // filter.
    return referenceResponseParts.filter(it => jsonRefIds.indexOf(it.refId) >= 0);
}
/**
 * Convert a response part to a message part.
 */
function convertToMessagePartReference(jsonRefResponsePart, referenceResponseParts) {
    return {
        kind: MessagePartKind.PART_REF,
        refId: jsonRefResponsePart.refId,
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        part: convertToMessagePart(jsonRefResponsePart, referenceResponseParts),
    };
}
/**
 * Convert a response part to a message part.
 */
function convertToMarkdownMessagePart(responsePart, referenceResponseParts) {
    // get only relevant reference response parts (ie. references in the markdown).
    const relevantResponseParts = filterRelevantMessagePartReferences(responsePart.text, referenceResponseParts);
    // the message parts.
    const messagePartsReferences = [];
    for (const responsePart of relevantResponseParts) {
        messagePartsReferences.push(convertToMessagePartReference(responsePart, referenceResponseParts));
    }
    return {
        kind: MessagePartKind.MARKDOWN,
        data: responsePart.text,
        messagePartsReferences,
    };
}
/**
 * Convert a response part to a message part.
 */
function convertToHighchartMessagePart(responsePart) {
    return {
        kind: MessagePartKind.HIGHCHARTS,
        data: responsePart.data,
    };
}
/**
 * Convert a response part to a message part.
 */
function convertToCustomMessagePart(responsePart) {
    // case of the json ref.
    if (isReferenceResponsePart(responsePart)) {
        return {
            kind: responsePart.type,
            data: responsePart.data,
        };
    }
    // case of a custom output.
    return {
        kind: responsePart.type,
        data: responsePart.data,
    };
}
/**
 * Convert a response part to a message part.
 */
function convertToEventMessagePart(responsePart) {
    return {
        kind: responsePart.type,
        data: responsePart.data,
    };
}
/**
 * Convert a response part to a message part.
 */
function convertToMessagePart(responsePart, referenceResponseParts) {
    // note that at this point, a response part can either be a reference or not.
    // mardown.
    if (isChatUiTextOutputResponsePart(responsePart)) {
        return convertToMarkdownMessagePart(responsePart, referenceResponseParts);
    }
    // highcharts.
    if (isChatUiHighchartsChartResponsePart(responsePart)) {
        return convertToHighchartMessagePart(responsePart);
    }
    // event.
    if (isEventResponsePart(responsePart)) {
        return convertToEventMessagePart(responsePart);
    }
    // custom.
    return convertToCustomMessagePart(responsePart);
}
/**
 * Parse the response.
 */
export function parseMessageParts(response) {
    // manage backward compatibility.
    const backwardCompatibleResult = parseBackwardCompatibleResponse(response);
    if (backwardCompatibleResult) {
        return backwardCompatibleResult;
    }
    // result array.
    const parts = [];
    // extract response references first.
    const referenceResponseParts = [];
    for (const responsePart of response.multiPartJson) {
        if (isReferenceResponsePart(responsePart)) {
            referenceResponseParts.push(responsePart);
        }
    }
    // then build the message parts.
    for (const multipartJsonItem of response.multiPartJson) {
        if (!isReferenceResponsePart(multipartJsonItem)) {
            parts.push(convertToMessagePart(multipartJsonItem, referenceResponseParts));
        }
    }
    return parts;
}
/**
 * Get the events from the response.
 */
export function parseEventMessageParts(response) {
    // manage backward compatibility.
    if (!isMultipartJsonResponse(response)) {
        return undefined;
    }
    // result array.
    const parts = [];
    // extract references first.
    for (const responsePart of response.multiPartJson) {
        if (isEventResponsePart(responsePart)) {
            parts.push(convertToEventMessagePart(responsePart));
        }
    }
    return parts;
}
//# sourceMappingURL=response-utils.js.map